import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, ChevronDown, ChevronUp, Clock, FileText, User, Grid, List, Filter, ExternalLink } from 'lucide-react';
import EmptyState from '../components/EmptyState';
import { apiRequest } from '../config/api';
import { buttonStyles } from '../utils/colors';
import SkeletonFormResponses from '../components/skeletons/SkeletonFormResponses';

function FormResponses() {
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [responses, setResponses] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedResponses, setExpandedResponses] = useState(new Set());
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [selectedVersions, setSelectedVersions] = useState([]);

  const toggleResponseExpansion = (responseId) => {
    const newExpanded = new Set(expandedResponses);
    if (newExpanded.has(responseId)) {
      newExpanded.delete(responseId);
    } else {
      newExpanded.add(responseId);
    }
    setExpandedResponses(newExpanded);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else if (diffInHours < 24 * 7) {
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [formResponse, responsesResponse, versionsResponse] = await Promise.all([
        apiRequest(`/forms/${id}`),
        apiRequest(`/responses/forms/${id}`),
        apiRequest(`/forms/${id}/versions`)
      ]);
      
      const formData = await formResponse.json();
      const responsesData = await responsesResponse.json();
      const versionsData = await versionsResponse.json();
      
      setForm(formData);
      setResponses(responsesData);
      setVersions(versionsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getVersionNumber = (versionId) => {
    if (!versionId) return null;
    const version = versions.find(v => v.id === versionId);
    return version ? version.version_number : null;
  };

  const getQuestionText = (fieldId, versionId) => {
    if (!versionId || !fieldId) return fieldId; // Fallback to field ID if no version info
    
    const version = versions.find(v => v.id === versionId);
    if (!version || !version.fields || !version.fields.steps) return fieldId;
    
    // Search through all steps in the version to find the field
    for (const step of version.fields.steps) {
      if (step.fields) {
        const field = step.fields.find(f => f.id === fieldId);
        if (field && field.label) {
          return field.label;
        }
      }
    }
    
    return fieldId; // Fallback to field ID if not found
  };

  // Get filtered responses based on selected versions
  const getFilteredResponses = () => {
    if (selectedVersions.length === 0) {
      return responses;
    }
    return responses.filter(response => 
      selectedVersions.includes(response.form_version_id)
    );
  };

  // Get all unique fields from filtered responses
  const getAllFields = () => {
    const filteredResponses = getFilteredResponses();
    const allFields = new Set();
    filteredResponses.forEach(response => {
      Object.keys(response.data).forEach(key => allFields.add(key));
    });
    return Array.from(allFields);
  };

  // Get column headers for table view
  const getTableColumns = () => {
    const fields = getAllFields();
    return fields.map(fieldId => {
      // Try to get question text from any response that has this field
      const responseWithField = getFilteredResponses().find(r => r.data[fieldId] !== undefined);
      if (responseWithField && responseWithField.form_version_id) {
        const questionText = getQuestionText(fieldId, responseWithField.form_version_id);
        return {
          fieldId,
          label: questionText !== fieldId ? questionText : fieldId
        };
      }
      return {
        fieldId,
        label: fieldId
      };
    });
  };

  const exportToCSV = () => {
    if (responses.length === 0) return;

    // Get all unique field keys from all responses
    const allKeys = new Set();
    responses.forEach(response => {
      Object.keys(response.data).forEach(key => allKeys.add(key));
    });

    // Create headers with question text instead of field IDs
    const questionHeaders = Array.from(allKeys).map(fieldId => {
      // Try to get question text from the first response that has this field
      const responseWithField = responses.find(r => r.data[fieldId] !== undefined);
      if (responseWithField && responseWithField.form_version_id) {
        const questionText = getQuestionText(fieldId, responseWithField.form_version_id);
        return questionText !== fieldId ? questionText : fieldId;
      }
      return fieldId;
    });

    const headers = ['Response ID', 'Version', 'Submitted At', ...questionHeaders];
    const csvContent = [
      headers.join(','),
      ...responses.map(response => {
        const row = [
          response.id,
          getVersionNumber(response.form_version_id) || 'Unknown',
          new Date(response.submitted_at).toLocaleString(),
          ...Array.from(allKeys).map(key => {
            const value = response.data[key];
            return Array.isArray(value) ? `"${value.join(', ')}"` : `"${value || ''}"`
          })
        ];
        return row.join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${form.title}_responses.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <SkeletonFormResponses />;

  if (!form) {
    return (
      <div className="text-center py-12">
        <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Form not found</h3>
        <p className="text-gray-500">The form you're looking for doesn't exist or you don't have access to it.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Forms
          </Link>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{form.title}</h1>
              <div className="flex items-center space-x-6 text-sm text-gray-500">
                <div className="flex items-center">
                  <FileText className="w-4 h-4 mr-1" />
                  <span className="font-medium text-gray-900">{responses.length}</span> responses
                </div>
                {form.status === 'published' && (
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-brand-500 rounded-full mr-2"></div>
                    Published
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-4 sm:mt-0 flex items-center space-x-3">
              {/* View Toggle */}
              {responses.length > 0 && (
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('card')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      viewMode === 'card'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Grid className="w-4 h-4 mr-1.5 inline" />
                    Cards
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      viewMode === 'table'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <List className="w-4 h-4 mr-1.5 inline" />
                    Table
                  </button>
                </div>
              )}
              
              {form.status === 'published' && (
                <a
                  href={`/public/forms/${id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${buttonStyles.outline} ${buttonStyles.small} inline-flex items-center`}
                >
                  View Form
                </a>
              )}
              {responses.length > 0 && (
                <button
                  onClick={exportToCSV}
                  className={`${buttonStyles.primary} ${buttonStyles.small} inline-flex items-center`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Version Filter for Table View */}
        {responses.length > 0 && viewMode === 'table' && versions.length > 1 && (
          <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <Filter className="w-4 h-4 text-gray-500 mr-2" />
                <span className="text-sm font-medium text-gray-700">Filter by version:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedVersions([])}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    selectedVersions.length === 0
                      ? 'bg-brand-100 text-brand-800 border border-brand-200'
                      : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                  }`}
                >
                  All Versions
                </button>
                {versions
                  .sort((a, b) => b.version_number - a.version_number)
                  .map(version => (
                    <button
                      key={version.id}
                      onClick={() => {
                        const isSelected = selectedVersions.includes(version.id);
                        if (isSelected) {
                          setSelectedVersions(selectedVersions.filter(id => id !== version.id));
                        } else {
                          setSelectedVersions([...selectedVersions, version.id]);
                        }
                      }}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        selectedVersions.includes(version.id)
                          ? 'bg-brand-100 text-brand-800 border border-brand-200'
                          : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      v{version.version_number}
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}

        {responses.length === 0 ? (
          <EmptyState
            illustration="responses"
            headline="No responses yet"
            description="Share your form to start collecting responses. Once people submit, their answers will appear here."
            customAction={
              form.status === 'published' ? (
                <div className="flex items-center gap-3">
                  <a
                    href={`/public/forms/${id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg text-white bg-brand-600 hover:bg-brand-700 transition-colors shadow-sm"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View Public Form
                  </a>
                </div>
              ) : null
            }
          />
        ) : (
          <div>
            {viewMode === 'card' ? (
              // Card View (existing implementation)
              <div className="space-y-4">
                {responses
                  .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))
                  .map((response, index) => {
                  const isExpanded = expandedResponses.has(response.id);
                  const responseEntries = Object.entries(response.data);
                  const previewEntries = responseEntries.slice(0, 2);
                  const hasMoreFields = responseEntries.length > 2;
                  
                  return (
                    <div 
                      key={response.id} 
                      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md"
                    >
                      {/* Response Header */}
                      <div className="px-6 py-4 border-b border-gray-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                              <div className="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-brand-600" />
                              </div>
                            </div>
                            
                            {response.form_version_id && (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                                v{getVersionNumber(response.form_version_id) || '?'}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center text-sm text-gray-500">
                              <Clock className="w-4 h-4 mr-1" />
                              {formatDate(response.submitted_at)}
                            </div>
                            
                            <button
                              onClick={() => toggleResponseExpansion(response.id)}
                              className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
                            >
                              {isExpanded ? 'Collapse' : 'Expand'}
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 ml-1" />
                              ) : (
                                <ChevronDown className="w-4 h-4 ml-1" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Response Preview (always visible) */}
                      <div className="px-6 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {previewEntries.map(([fieldId, value]) => (
                            <div key={fieldId} className="space-y-1">
                              <dt className="text-sm font-medium text-gray-600">
                                {getQuestionText(fieldId, response.form_version_id)}
                              </dt>
                              <dd className="text-sm text-gray-900 break-words">
                                {Array.isArray(value) ? (
                                  <div className="flex flex-wrap gap-1">
                                    {value.map((item, idx) => (
                                      <span key={idx} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                                        {item}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="block">{value?.toString() || '-'}</span>
                                )}
                              </dd>
                            </div>
                          ))}
                        </div>
                        
                        {hasMoreFields && !isExpanded && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <button
                              onClick={() => toggleResponseExpansion(response.id)}
                              className="text-sm text-brand-600 hover:text-brand-700 font-medium transition-colors"
                            >
                              View {responseEntries.length - 2} more field{responseEntries.length - 2 !== 1 ? 's' : ''}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && hasMoreFields && (
                        <div className="px-6 pb-4 border-t border-gray-100 bg-gray-50">
                          <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {responseEntries.slice(2).map(([fieldId, value]) => (
                              <div key={fieldId} className="space-y-1">
                                <dt className="text-sm font-medium text-gray-600">
                                  {getQuestionText(fieldId, response.form_version_id)}
                                </dt>
                                <dd className="text-sm text-gray-900 break-words">
                                  {Array.isArray(value) ? (
                                    <div className="flex flex-wrap gap-1">
                                      {value.map((item, idx) => (
                                        <span key={idx} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                                          {item}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="block">{value?.toString() || '-'}</span>
                                  )}
                                </dd>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              // Table View
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Submitted
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Version
                        </th>
                        {getTableColumns().map(column => (
                          <th
                            key={column.fieldId}
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {column.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getFilteredResponses()
                        .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))
                        .map((response, index) => (
                        <tr key={response.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 text-gray-400 mr-2" />
                              {formatDate(response.submitted_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                              v{getVersionNumber(response.form_version_id) || '?'}
                            </span>
                          </td>
                          {getTableColumns().map(column => (
                            <td
                              key={`${response.id}-${column.fieldId}`}
                              className="px-6 py-4 text-sm text-gray-900 max-w-xs"
                            >
                              {response.data[column.fieldId] !== undefined ? (
                                Array.isArray(response.data[column.fieldId]) ? (
                                  <div className="flex flex-wrap gap-1">
                                    {response.data[column.fieldId].map((item, idx) => (
                                      <span key={idx} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                                        {item}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="truncate" title={response.data[column.fieldId]?.toString()}>
                                    {response.data[column.fieldId]?.toString() || '-'}
                                  </div>
                                )
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {getFilteredResponses().length === 0 && (
                  <div className="text-center py-12">
                    <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No responses match the selected filters</h3>
                    <p className="text-gray-500">Try selecting different form versions or clear the filters.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default FormResponses;
